import { add, startOfDay, startOfHour, startOfMinute, sub } from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

export type FirstDayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const WEEK_DAYS: { value: FirstDayOfWeek; label: string }[] = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export const time_unit_to_abbreviation = {
  second: 'sec',
  minute: 'min',
  hour: 'hr',
  month: 'mo',
  day: 'd',
  week: 'wk',
  year: 'yr',
  concurrent: 'concurrent',
};

export const relative_date_presets = [
  'last_minute',
  'last_5_minutes',
  'last_15_minutes',
  'last_30_minutes',
  'last_hour',
  'last_3_hours',
  'last_6_hours',
  'last_12_hours',
  'yesterday',
  'last_3_days',
  'last_7_days',
  'last_2_weeks',
  'last_month',
] as const;

type RelativeDatePreset = (typeof relative_date_presets)[number];
type LegacyRelativeDate = 'today';

export type RelativeDate = RelativeDatePreset | LegacyRelativeDate;

const getEndOfCurrentMinute = (now: Date) => add(startOfMinute(now), { minutes: 1 });
const getEndOfCurrentHour = (now: Date) => add(startOfHour(now), { hours: 1 });

const convertMinutes = (minutes: number) => (now: Date) => {
  const max = getEndOfCurrentMinute(now);
  return {
    min: sub(max, { minutes }),
    max,
  };
};

const convertHours = (hours: number) => (now: Date) => {
  const max = getEndOfCurrentMinute(now);
  return {
    min: sub(max, { hours }),
    max,
  };
};

const convertDays = (days: number) => (now: Date) => {
  const max = getEndOfCurrentHour(now);
  return {
    min: sub(max, { days }),
    max,
  };
};

export const relative_dates: {
  [key in RelativeDate]: {
    label: string;
    short_label: string;
    days: number;
    convert: (now: Date) => { min: Date; max: Date };
  };
} = {
  last_minute: {
    label: 'Last minute',
    short_label: '1 min',
    days: 0,
    convert: convertMinutes(1),
  },
  last_5_minutes: {
    label: 'Last 5 minutes',
    short_label: '5 mins',
    days: 0,
    convert: convertMinutes(5),
  },
  last_15_minutes: {
    label: 'Last 15 minutes',
    short_label: '15 mins',
    days: 0,
    convert: convertMinutes(15),
  },
  last_30_minutes: {
    label: 'Last 30 minutes',
    short_label: '30 mins',
    days: 0,
    convert: convertMinutes(30),
  },
  last_hour: {
    label: 'Last hour',
    short_label: '1 hour',
    days: 0,
    convert: convertHours(1),
  },
  last_3_hours: {
    label: 'Last 3 hours',
    short_label: '3 hrs',
    days: 0,
    convert: convertHours(3),
  },
  last_6_hours: {
    label: 'Last 6 hours',
    short_label: '6 hrs',
    days: 0,
    convert: convertHours(6),
  },
  last_12_hours: {
    label: 'Last 12 hours',
    short_label: '12 hrs',
    days: 0,
    convert: convertHours(12),
  },
  today: {
    label: 'Today',
    short_label: 'today',
    days: 0,
    convert: (now) => ({
      min: startOfDay(now),
      max: add(startOfHour(now), { hours: 1 }),
    }),
  },
  yesterday: {
    label: 'Last day',
    short_label: '1 day',
    days: 1,
    convert: convertDays(1),
  },
  last_3_days: {
    label: 'Last 3 days',
    short_label: '3 days',
    days: 3,
    convert: convertDays(3),
  },
  last_7_days: {
    label: 'Last 7 days',
    short_label: '7 days',
    days: 7,
    convert: convertDays(7),
  },
  last_2_weeks: {
    label: 'Last 14 days',
    short_label: '14 days',
    days: 14,
    convert: convertDays(14),
  },
  last_month: {
    days: 30,
    label: 'Last 30 days',
    short_label: '30 days',
    convert: convertDays(30),
  },
};

export const resolveRelativeDate = (
  key: string,
  default_date: RelativeDate = 'yesterday',
  reference_date: Date = new Date(),
): { relative: RelativeDate; min: Date; max: Date } => {
  const resolved = relative_dates[key as RelativeDate] ? (key as RelativeDate) : default_date;
  const { min, max } = relative_dates[resolved].convert(reference_date);
  return { relative: resolved, min, max };
};

export const addTimeToDate = (date: Date | string, time: string) => {
  const [h, m, s] = time.split(':');
  const new_date = new Date(date);
  new_date.setHours(parseInt(h ?? '00'), parseInt(m ?? '00'), parseInt(s ?? '00'));
  return new_date;
};

export const setTimeInTimezone = (date: Date | string, time: string, timezone: string): Date => {
  const zoned_date = utcToZonedTime(date, timezone);
  return zonedTimeToUtc(addTimeToDate(zoned_date, time), timezone);
};

export const getLocaleFirstDayOfWeek = (): FirstDayOfWeek => {
  if (typeof navigator === 'undefined') {
    return 1;
  }
  try {
    const locale = new Intl.Locale(navigator.language);
    // Firefox implements the newer getWeekInfo() method; Chromium and WebKit
    // expose the same data as a weekInfo property.
    const week_info =
      locale.getWeekInfo?.() ?? (locale as Intl.Locale & { weekInfo?: Intl.WeekInfo }).weekInfo;
    if (!week_info) {
      return 1;
    }
    // WeekInfo.firstDay is 1 (Monday) to 7 (Sunday); convert to 0 (Sunday) to 6 (Saturday).
    return (week_info.firstDay % 7) as FirstDayOfWeek;
  } catch {
    return 1;
  }
};

export const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const getSupportedTimezones = () => {
  const supported_values_of = (
    Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] }
  ).supportedValuesOf;
  const browser_timezone = getBrowserTimezone();
  const timezones = supported_values_of?.('timeZone') ?? [browser_timezone, 'UTC'];

  return Array.from(new Set([browser_timezone, 'UTC', ...timezones])).sort((a, b) =>
    a.localeCompare(b),
  );
};

export const getTimezoneUtcOffset = (timezone: string, date: Date | string | number = new Date()) =>
  `UTC${formatInTimeZone(date, timezone, 'xxx')}`;

const TIMEZONE_ABBREVIATION_LOCALES = ['en-CA', 'en-US', 'en-GB', 'en-AU'];
const TIMEZONE_OFFSET_NAME_PATTERN = /^(?:GMT|UTC)[+-]/;

export const formatDate = (
  date: Date | string | number,
  format_string: string,
  timezone = getBrowserTimezone(),
) => formatInTimeZone(date, timezone, format_string);

export const getTimezoneAbbreviation = (
  timezone = getBrowserTimezone(),
  date: Date | string | number = new Date(),
) => {
  const instant = new Date(date);
  let offset_name: string | undefined;

  for (const locale of TIMEZONE_ABBREVIATION_LOCALES) {
    const timezone_name = new Intl.DateTimeFormat(locale, {
      timeZone: timezone,
      timeZoneName: 'short',
    })
      .formatToParts(instant)
      .find(({ type }) => type === 'timeZoneName')?.value;

    if (!timezone_name) {
      continue;
    }
    if (!TIMEZONE_OFFSET_NAME_PATTERN.test(timezone_name)) {
      return timezone_name;
    }
    offset_name ??= timezone_name;
  }

  return offset_name ?? getTimezoneUtcOffset(timezone, instant).replace('UTC', 'GMT');
};

export const getTimezoneLabel = (timezone: string, date: Date | string | number = new Date()) =>
  `${getTimezoneAbbreviation(timezone, date)} (${getTimezoneUtcOffset(timezone, date)})`;

export const getCurrentTimezoneAbreviation = getTimezoneAbbreviation;

export const getWeekDayLabel = (day: FirstDayOfWeek) => {
  return WEEK_DAYS.find(({ value }) => value === day)?.label ?? 'Monday';
};
